/**
 * The review strip under the composer after io.ai writes into the document:
 * how many revisions are waiting, Next to read them in place, Accept all and
 * Reject all. With WordApi 1.6 the count is live and the actions work from
 * the pane; on older hosts the strip only reports what was written.
 */
import { CheckIcon, ChevronRightIcon, XIcon } from "@/design/icons";
import { type FC, type ReactNode, useEffect, useState } from "react";

import { cn } from "@/agents-v2/utils/cn";

import { type ReviewScope, reviewChanges, selectSuggestion } from "../../document/review";
import { documentCapabilities } from "../../document/snapshot";
import type { EditNotice } from "../../document/useWordToolInterrupts";

export const ReviewStrip: FC<{
  notice: EditNotice;
  scope: ReviewScope;
  onDismiss: () => void;
  onDocumentChanged: () => void;
}> = ({ notice, scope, onDismiss, onDocumentChanged }) => {
  const live = documentCapabilities().review;
  const [count, setCount] = useState<number | null>(null);
  const [busy, setBusy] = useState<"accept" | "reject" | "next" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshCount = async () => {
    if (!live) return;
    try {
      const result = await reviewChanges("count", scope);
      setCount(result.count);
    } catch {
      setCount(null);
    }
  };
  useEffect(() => {
    void refreshCount();
    // Recount for each new batch of edits.
  }, [notice.at, scope.author]);

  const resolve = async (action: "accept_all" | "reject_all") => {
    // Without authored revisions the scope is every tracked change in the
    // document, the user's own included: say so and ask first.
    if (
      scope.author === null &&
      !window.confirm(
        `This will ${action === "accept_all" ? "accept" : "reject"} every tracked change in the document, including changes you made yourself. Continue?`
      )
    )
      return;
    setBusy(action === "accept_all" ? "accept" : "reject");
    setError(null);
    try {
      await reviewChanges(action, scope);
      onDocumentChanged();
      onDismiss();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Word could not resolve the changes");
    } finally {
      setBusy(null);
    }
  };
  const next = async () => {
    setBusy("next");
    try {
      await selectSuggestion(scope);
    } finally {
      setBusy(null);
    }
  };

  const n = live && count !== null ? count : notice.applied;
  const all = scope.author === null;
  if (live && count === 0) {
    return (
      <div className="mt-2 flex items-center gap-2 rounded-sm border border-line bg-subtle px-2.5 py-2 text-xs text-fg-2">
        <CheckIcon className="size-3.5 text-success" />
        <span className="flex-1">All of io.ai&apos;s changes have been reviewed.</span>
        <button type="button" className="font-semibold text-fg-2 hover:text-fg" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-sm border border-accent/30 bg-accent-soft px-2.5 py-2 text-xs text-fg">
      <div className="flex items-center gap-2">
        <span className="size-1.5 shrink-0 rounded-full bg-accent" />
        <span className="min-w-0 flex-1 truncate">
          <span className="font-semibold">
            {n} {n === 1 ? "change" : "changes"}
          </span>{" "}
          {notice.kind === "formatting" ? "of formatting " : ""}
          waiting for your review
          {live && all ? " (every tracked change, yours included)" : ""}
          {notice.stale ? ` · ${notice.stale} skipped` : ""}
        </span>
        <button type="button" aria-label="Dismiss" className="shrink-0 text-fg-3 hover:text-fg" onClick={onDismiss}>
          <XIcon className="size-3.5" />
        </button>
      </div>
      {live && (
        <div className="mt-1.5 flex items-center gap-1">
          <StripButton onClick={() => void next()} disabled={busy !== null}>
            Next
            <ChevronRightIcon className="size-3" />
          </StripButton>
          <span className="flex-1" />
          <StripButton onClick={() => void resolve("reject_all")} disabled={busy !== null}>
            {busy === "reject" ? "Rejecting…" : "Reject all"}
          </StripButton>
          <StripButton primary onClick={() => void resolve("accept_all")} disabled={busy !== null}>
            {busy === "accept" ? "Accepting…" : "Accept all"}
          </StripButton>
        </div>
      )}
      {notice.authoredError && (
        <div className="mt-1 text-fg-3">
          Some were not applied: Word refused them as mytender.io changes ({notice.authoredError}).
        </div>
      )}
      {error && <div className="mt-1 text-danger">{error}</div>}
    </div>
  );
};

const StripButton: FC<{ primary?: boolean; disabled?: boolean; onClick: () => void; children: ReactNode }> = ({
  primary,
  disabled,
  onClick,
  children,
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "inline-flex h-6 items-center gap-0.5 rounded-sm px-2 text-xs font-semibold transition-colors duration-fast disabled:opacity-50",
      primary ? "bg-fg text-fg-inverse hover:bg-fg/90" : "text-fg-2 hover:bg-accent-soft-2 hover:text-fg"
    )}
  >
    {children}
  </button>
);
